import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, HostListener, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IssueManagementService } from './issue-management.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-issue-edit',
    templateUrl: './issue-edit.component.html',
    styleUrls: ['./issue-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class IssueEditComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = false;
  loadingData = true;

  // Modal data
  modalTitle = 'Edit Issue Details';
  modalData: any = {};
  rowData: any = {};
  taxYears: any[] = [];
  selectedTaxYear: any;
  issuesData: any[] = [];
  multiYearDropDownOptions: any[] = [];
  adjTypeOptions: any[] = [];
  timingOptions: any[] = [];

  // Form data
  shortDescription: string = '';
  detailedDescription: string = '';
  selectedStdDesc: any = null;
  primaryCitation: string = '';
  multiYear: boolean = false;
  multiYearDropDown: any = null;
  adjType: any = null;
  filedScheduleFormCheck: boolean = false;
  timing: any = null;
  effectYrs: string = 'No';
  explanation: string = '';

  // Tax year scenarios
  taxYearScenarios: any[] = [];
  taxYearScenariosCopy: any[] = [];
  selectdTYScenario: any[] = [];
  invalidTaxYearScenarios: boolean = false;

  // Original values for reset functionality
  private originalValues: any = {};

  // Dropdown list
  stdDescList: any[] = [];
  adjustmentTypes: any[] = [];
  selectedType: any = null;

  // Validation flags
  isShortDescUnique: boolean = true;
  isDetailedDescUnique: boolean = true;

  // Config data
  GENERAL_CONFIG: any = {};
  globalService: any = {};
  baseUrl: string = '';

  // Initialization flags
  private rowDataReceived = false;
  private modalDataReceived = false;
  private initialized = false;

  constructor(
    private issueService: IssueManagementService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    // Close all dropdowns when clicking outside
    this.taxYearScenarios.forEach(item => {
      item.dropdownOpen = false;
    });
    this.cdr.detectChanges();
  }

  @Input('base-urls')
  set _base_urls(urls: string) {
    try {
      const urlsObject = safeJsonInput<any>(urls, null, 'issue-edit.parse1');
      if (Object.keys(urlsObject).length > 0) {
        this.baseUrl = urlsObject.base_url || '';
        this.tryInitialize();
      }
    } catch (e) {
      console.log("Base URLs input not yet stable. Still waiting to get resolved.");
    }
  }

  @Input('header-data')
  set _header_data(headerData: string) {
    try {
      const data = safeJsonInput<any>(headerData, null, 'issue-edit.parse2');
      if (Object.keys(data).length > 0) {
        this.globalService = data.globalParams || {};
        this.GENERAL_CONFIG = data.generalConfigData || {};
        if (!this.baseUrl && this.GENERAL_CONFIG.base_url) {
          this.baseUrl = this.GENERAL_CONFIG.base_url;
        }
        this.tryInitialize();
      }
    } catch (e) {
      console.log("Header data input not yet stable. Still waiting to get resolved.");
    }
  }

  @Input('row-data')
  set _row_data(rowData: string) {
    try {
      const data = safeJsonInput<any>(rowData, null, 'issue-edit.parse3');
      if (Object.keys(data).length > 0) {
        this.rowData = data;
        this.shortDescription = data.issue_short_desc || '';
        this.detailedDescription = data.issue_desc || '';
        this.rowDataReceived = true;
        this.tryInitialize();
      }
    } catch (e) {
      console.log("Row data input not yet stable. Still waiting to get resolved.");
    }
  }

  @Input('modal-data')
  set _modal_data(modalData: string) {
    try {
      const data = safeJsonInput<any>(modalData, null, 'issue-edit.parse4');
      if (Object.keys(data).length > 0) {
        this.modalData = data;
        
        // Convert taxYears from object to array if needed (AngularJS serialization issue)
        this.taxYears = this.convertToArray(data.taxYears) || [];
        this.issuesData = this.convertToArray(data.issuesData) || [];
        this.multiYearDropDownOptions = this.convertToArray(data.multiYearDropDownOptions) || [];
        this.adjTypeOptions = this.convertToArray(data.adjTypeOptions) || [];
        this.timingOptions = this.convertToArray(data.timingOptions) || [];
        
        this.modalDataReceived = true;
        this.tryInitialize();
      }
    } catch (e) {
      console.log("Modal data input not yet stable. Still waiting to get resolved.");
    }
  }

  // Helper function to convert object with numeric keys to array
  convertToArray(obj: any): any[] {
    if (Array.isArray(obj)) {
      return obj;
    }
    if (obj && typeof obj === 'object') {
      // Check if it's an object with numeric keys (serialized array)
      const keys = Object.keys(obj);
      if (keys.length > 0 && keys.every(key => !isNaN(Number(key)))) {
        return Object.values(obj);
      }
    }
    return obj || [];
  }

  ngOnInit() {
    // Component initialization
  }

  tryInitialize() {
    // Only initialize once when both rowData and modalData are received
    if (!this.initialized && this.rowDataReceived && this.modalDataReceived && this.baseUrl) {
      this.initialized = true;
      this.init();
    }
  }

  onShortDescriptionChange() {
    if (this.shortDescription && this.shortDescription.trim()) {
      // Check uniqueness, but exclude the current issue being edited
      this.isShortDescUnique = !this.issuesData.some(
        issue => issue.issue_short_desc === this.shortDescription && 
                 issue.issue_id !== this.rowData.issue_id
      );
    } else {
      this.isShortDescUnique = true;
    }
  }

  onDetailedDescriptionChange() {
    if (this.detailedDescription && this.detailedDescription.trim()) {
      // Check uniqueness, but exclude the current issue being edited
      this.isDetailedDescUnique = !this.issuesData.some(
        issue => issue.issue_desc === this.detailedDescription && 
                 issue.issue_id !== this.rowData.issue_id
      );
    } else {
      this.isDetailedDescUnique = true;
    }
  }

  init() {
    try {
      if (this.baseUrl && this.rowData && this.rowData.issue_id) {
        // Find selected tax year
        if (Array.isArray(this.taxYears) && this.taxYears.length > 0) {
          this.selectedTaxYear = this.taxYears.find(ty => ty.value === this.rowData.tax_year.toString());
          // Ensure null if no match found (not undefined)
          if (!this.selectedTaxYear) {
            this.selectedTaxYear = null;
          }
        }
        
        this.loadAdjustmentTypes();
        this.getStdDescList();
        this.getTYScenariosAndDetails();
      }
    } catch (e) {
      console.error('Error in issue-edit init:', e);
      this.loadingData = false;
      this.cdr.detectChanges();
    }
  }

  loadAdjustmentTypes() {
    if (!this.selectedTaxYear || !this.baseUrl) return;
    
    this.issueService.getScenarios(this.baseUrl, this.selectedTaxYear.value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === '1') {
          this.adjustmentTypes = response.scenarioList || [];
          this.selectedType = this.adjustmentTypes.find(
            at => at.scenario === this.rowData.scenario.toString()
          );
          // Ensure null if no match found (not undefined)
          if (!this.selectedType) {
            this.selectedType = null;
          }
          // Trigger validation when selectedType changes
          this.onSelectedTypeChange();
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading adjustment types:', error);
      }
    );
  }

  onSelectedTypeChange() {
    // Re-validate descriptions when selectedType changes (like AngularJS $watch)
    if (this.selectedType) {
      this.onShortDescriptionChange();
      this.onDetailedDescriptionChange();
    }
  }

  getTYScenariosAndDetails() {
    if (!this.baseUrl) return;

    this.loadingData = true;
    
    this.issueService.getTYScenarios(this.baseUrl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === '1') {
          this.taxYearScenarios = response.taxyearScenarioList || [];
          
          // Load issue details
          this.issueService.getIssueDetails(this.baseUrl, this.rowData.issue_id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (detailsRes: any) => {
              // Handle both response formats
              const issueData = detailsRes.data?.issue || detailsRes.issue || {};
              const scList = issueData.issueTyScenarioList || [];
              
              // Map selected scenarios
              this.taxYearScenarios.forEach(tyScenario => {
                tyScenario.dropdownOpen = false;
                const matchingYear = scList.find((sc: any) => sc.tax_year === tyScenario.tax_year);
                if (matchingYear) {
                  const tyScList = matchingYear.scenarioList.map((s: any) => s.scenario);
                  tyScenario.selectedScenario = tyScenario.scenarioList.filter(
                    (s: any) => tyScList.indexOf(s.scenario) > -1
                  );
                } else {
                  tyScenario.selectedScenario = [];
                }
              });

              // Store copy for reset
              this.taxYearScenariosCopy = safeJsonInput<any>(JSON.stringify(this.taxYearScenarios), null, 'issue-edit.parse5');

              // Load other issue details with better type handling
              this.primaryCitation = issueData.primary_citation || '';
              this.multiYear = issueData.multi_yr_check === "true" || issueData.multi_yr_check === true;
              
              if (this.multiYear) {
                const multiYrValue = issueData.multi_yr;
                this.multiYearDropDown = multiYrValue ? this.multiYearDropDownOptions.find(
                  opt => opt.value === multiYrValue
                ) : null;
                // Ensure null if no match found (not undefined)
                if (!this.multiYearDropDown) {
                  this.multiYearDropDown = null;
                }
              } else {
                this.multiYearDropDown = null;
              }

              const adjTypeValue = issueData.adj_type;
              this.adjType = adjTypeValue ? this.adjTypeOptions.find(
                opt => opt.CODE_DESC === adjTypeValue
              ) : null;
              // Ensure null if no match found (not undefined)
              if (!this.adjType) {
                this.adjType = null;
              }

              this.filedScheduleFormCheck = issueData.filed_schedule_form_check === "true" || issueData.filed_schedule_form_check === true;

              const timingValue = issueData.timing;
              this.timing = timingValue ? this.timingOptions.find(
                opt => opt.value === timingValue
              ) : null;
              // Ensure null if no match found (not undefined)
              if (!this.timing) {
                this.timing = null;
              }

              this.effectYrs = issueData.effect_other_yrs || 'No';
              this.explanation = issueData.explanation || '';

              // Build selected scenario list
              this.selectdTYScenario = [];
              this.taxYearScenarios.forEach(item => {
                if (item.selectedScenario && item.selectedScenario.length > 0) {
                  this.selectdTYScenario.push(item.selectedScenario);
                }
              });
              
              this.invalidTaxYearScenarios = this.selectdTYScenario.length === 0;

              // Store all original values for reset
              this.storeOriginalValues();

              this.loadingData = false;
              this.cdr.detectChanges();
            },
            (error: any) => {
              console.error('Error loading issue details:', error);
              this.loadingData = false;
              this.cdr.detectChanges();
            }
          );
        }
      },
      (error: any) => {
        console.error('Error loading tax year scenarios:', error);
        this.loadingData = false;
        this.cdr.detectChanges();
      }
    );
  }

  getStdDescList() {
    if (!this.baseUrl) return;

    this.issueService.getStdDesc(this.baseUrl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === '1') {
          this.stdDescList = response.stdDescList || [];
          
          // Try both string and number comparison
          this.selectedStdDesc = this.stdDescList.find(
            std => std.std_desc_id === this.rowData.std_desc_id.toString() || 
                   std.std_desc_id === this.rowData.std_desc_id ||
                   std.std_desc_id.toString() === this.rowData.std_desc_id.toString()
          );
          
          // Ensure null if no match found (not undefined)
          if (!this.selectedStdDesc) {
            this.selectedStdDesc = null;
          }
          
          // Update original value for selectedStdDesc if it was loaded after storeOriginalValues was called
          if (this.originalValues && Object.keys(this.originalValues).length > 0) {
            this.originalValues.selectedStdDesc = this.selectedStdDesc;
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading standard descriptions:', error);
      }
    );
  }

  onScenarioSelectionChanged() {
    this.selectdTYScenario = [];
    this.taxYearScenarios.forEach(item => {
      if (item.selectedScenario && item.selectedScenario.length > 0) {
        this.selectdTYScenario.push(item.selectedScenario);
      }
    });

    this.invalidTaxYearScenarios = this.selectdTYScenario.length === 0;
  }

  toggleDropdown(index: number, event: Event) {
    event.stopPropagation();
    // Close all other dropdowns
    this.taxYearScenarios.forEach((item, idx) => {
      if (idx !== index) {
        item.dropdownOpen = false;
      }
    });
    // Toggle current dropdown
    this.taxYearScenarios[index].dropdownOpen = !this.taxYearScenarios[index].dropdownOpen;
    this.cdr.detectChanges();
  }

  getSelectedText(item: any): string {
    if (!item.selectedScenario || item.selectedScenario.length === 0) {
      return 'Select scenario';
    }
    if (item.selectedScenario.length === 1) {
      return item.selectedScenario[0].scenario_desc;
    }
    return `${item.selectedScenario.length} selected`;
  }

  isSelected(item: any, scenario: any): boolean {
    return item.selectedScenario && item.selectedScenario.some((s: any) => s.scenario === scenario.scenario);
  }

  toggleSelection(item: any, scenario: any) {
    if (!item.selectedScenario) {
      item.selectedScenario = [];
    }
    
    const index = item.selectedScenario.findIndex((s: any) => s.scenario === scenario.scenario);
    if (index > -1) {
      item.selectedScenario.splice(index, 1);
    } else {
      item.selectedScenario.push(scenario);
    }
    
    this.onScenarioSelectionChanged();
    this.cdr.detectChanges();
  }

  selectAll(item: any) {
    item.selectedScenario = [...item.scenarioList];
    this.onScenarioSelectionChanged();
    this.cdr.detectChanges();
  }

  unselectAll(item: any) {
    item.selectedScenario = [];
    this.onScenarioSelectionChanged();
    this.cdr.detectChanges();
  }

  toggleMultiYear() {
    if (this.multiYear && this.multiYearDropDownOptions.length > 0) {
      this.multiYearDropDown = this.multiYearDropDownOptions[0];
    } else {
      this.multiYearDropDown = null;
    }
  }

  isFormValid(): boolean {
    return !!(
      this.shortDescription &&
      this.shortDescription.trim().length > 0 &&
      this.shortDescription.length <= 35 &&
      this.isShortDescUnique &&
      this.detailedDescription &&
      this.detailedDescription.trim().length > 0 &&
      this.isDetailedDescUnique &&
      this.selectedStdDesc &&
      !this.invalidTaxYearScenarios
    );
  }

  save() {
    if (!this.isFormValid()) {
      this._snackBar.open('Please fill all required fields', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    // Build tax year object
    const txYrObj: any = {};
    this.selectdTYScenario.forEach(scenarioGroup => {
      scenarioGroup.forEach((scenario: any) => {
        txYrObj[scenario.tax_year] = txYrObj[scenario.tax_year] || [];
        txYrObj[scenario.tax_year].push(scenario.scenario);
      });
    });

    // Build parameters string
    let codeSeqMapStr = '';
    for (const year in txYrObj) {
      codeSeqMapStr += `${year}=${txYrObj[year].toString()}&`;
    }
    codeSeqMapStr += `issue_desc=${this.detailedDescription}&`;
    codeSeqMapStr += `issue_short_desc=${this.shortDescription}&`;
    codeSeqMapStr += `std_desc_id=${this.selectedStdDesc.std_desc_id}&`;
    codeSeqMapStr += `issue_id=${this.rowData.issue_id}&`;
    codeSeqMapStr += `primary_citation=${this.primaryCitation ? this.primaryCitation : ''}&`;
    codeSeqMapStr += `multi_yr=${this.multiYear && this.multiYearDropDown ? this.multiYearDropDown.value : 'NA'}&`;
    codeSeqMapStr += `multi_yr_check=${this.multiYear}&`;
    codeSeqMapStr += `adj_type=${this.adjType ? this.adjType.CODE_DESC : ''}&`;
    codeSeqMapStr += `filed_schedule_form_check=${this.filedScheduleFormCheck}&`;
    codeSeqMapStr += `timing=${this.timing ? this.timing.value : ''}&`;
    codeSeqMapStr += `effect_other_yrs=${this.effectYrs}&`;
    codeSeqMapStr += `explanation=${this.explanation ? this.explanation : ''}`;

    this.issueService.updateIssue(this.baseUrl, codeSeqMapStr).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess === "0") {
          this._snackBar.open(response.errorMessage || 'Error occurred while updating', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Issue Updated Successfully!', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error updating issue:', error);
        this._snackBar.open('Something went wrong while updating. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  storeOriginalValues() {
    // Store all original values for reset functionality
    this.originalValues = {
      shortDescription: this.shortDescription,
      detailedDescription: this.detailedDescription,
      selectedStdDesc: this.selectedStdDesc,
      primaryCitation: this.primaryCitation,
      multiYear: this.multiYear,
      multiYearDropDown: this.multiYearDropDown,
      adjType: this.adjType,
      filedScheduleFormCheck: this.filedScheduleFormCheck,
      timing: this.timing,
      effectYrs: this.effectYrs,
      explanation: this.explanation,
      taxYearScenarios: safeJsonInput<any>(JSON.stringify(this.taxYearScenarios), null, 'issue-edit.parse6'),
      selectdTYScenario: safeJsonInput<any>(JSON.stringify(this.selectdTYScenario), null, 'issue-edit.parse7'),
      invalidTaxYearScenarios: this.invalidTaxYearScenarios
    };
  }

  reset() {
    // Restore all original values
    this.shortDescription = this.originalValues.shortDescription || '';
    this.detailedDescription = this.originalValues.detailedDescription || '';
    this.selectedStdDesc = this.originalValues.selectedStdDesc;
    this.primaryCitation = this.originalValues.primaryCitation || '';
    this.multiYear = this.originalValues.multiYear || false;
    this.multiYearDropDown = this.originalValues.multiYearDropDown;
    this.adjType = this.originalValues.adjType;
    this.filedScheduleFormCheck = this.originalValues.filedScheduleFormCheck || false;
    this.timing = this.originalValues.timing;
    this.effectYrs = this.originalValues.effectYrs || 'No';
    this.explanation = this.originalValues.explanation || '';
    this.taxYearScenarios = safeJsonInput<any>(JSON.stringify(this.originalValues.taxYearScenarios), null, 'issue-edit.parse8');
    this.selectdTYScenario = safeJsonInput<any>(JSON.stringify(this.originalValues.selectdTYScenario), null, 'issue-edit.parse9');
    this.invalidTaxYearScenarios = this.originalValues.invalidTaxYearScenarios || false;
    
    // Reset validation flags
    this.isShortDescUnique = true;
    this.isDetailedDescUnique = true;
    
    // Trigger change detection
    this.cdr.detectChanges();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}

